import React, { useEffect, useState, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import SnackMessage from './components/SnackMessage';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { auth, db, doc, setDoc, getDoc, updateDoc, onSnapshot, collection, getDocs, writeBatch } from './components/FirebaseConfig';
import logger from './helpers/logger';
import { Box, CircularProgress } from '@mui/material';
import { sanitizePlaylistName, getActivePlaylistSnapshot, resolveNextTrack } from './utils/playlistUtils';

// Helper para resetear el scroll al tope en cada cambio de ruta (excepto si tiene hash)
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
}

import RouteTracker from './components/common/RouteTracker';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { I18nProvider } from './i18n';

// Pages
import Config from './pages/Config';
import Player from './pages/Player';
import Pannel from './pages/Pannel';
import Login from './pages/Login';
import Register from './pages/Register';
import NotFound from './pages/NotFound';
import Landing from './pages/Landing';
import Pricing from './pages/Pricing';
import Support from './pages/Support';
import Monitor from './pages/Monitor';
import MonitorLogin from './pages/MonitorLogin';
import RemoteControl from './pages/RemoteControl';
import MobileConsoleNotice from './pages/MobileConsoleNotice';
import { Howl } from 'howler';
import { apiBaseUrl, isSyncBackendAvailable } from './services/api';
import { getAppTitle } from './utils/version';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#ff2d9b',
      light: '#ff66be',
      dark: '#d61a7a',
    },
    secondary: {
      main: '#b44dff',
      light: '#cf85ff',
      dark: '#9121eb',
    },
    background: {
      default: '#06010e',
      paper: '#0f081d', // 100% lleno, sin transparencia
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h6: {
      fontFamily: "'Outfit', sans-serif",
      fontWeight: 700,
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#0f081d !important',
          backgroundImage: 'none !important',
          backdropFilter: 'none !important',
          border: '1px solid rgba(168, 85, 247, 0.35)',
          boxShadow: '0 25px 70px rgba(0, 0, 0, 0.98), 0 0 40px rgba(0, 0, 0, 0.85)',
          borderRadius: 16,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          marginBottom: '16px',
        },
      },
    },
  },
});

const emptyUserSettings = {
  configs: {
    general: {
      showControlsPlayer: true,
      allowInteractivity: true,       
      theme: 'dark',
    },
    screenSettings: {
      volume: 100,
      playbackRate: 1.0,
      pitch: 0,
    },
    apiKeys: Array(5).fill(''),
    intros: Array(5).fill(''),
    transitions: Array(5).fill(''),
  },
  playlist: [],
  presetPlaylists: [],
};

function MainApp() {
  const location = useLocation();
  const { user, loading: authLoading, signOut, hasActivePlan } = useAuth();
  const [bypassMobileNotice, setBypassMobileNotice] = useState(() => {
    try {
      return sessionStorage.getItem('garaoke_bypass_mobile_notice') === 'true';
    } catch (e) {
      return false;
    }
  });

  const [isMobileScreen, setIsMobileScreen] = useState(() => {
    if (typeof window === 'undefined') return false;
    const isSmall = window.innerWidth < 768;
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    return isSmall || isMobileUA;
  });

  useEffect(() => {
    document.title = getAppTitle('Garaoke');
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const isSmall = window.innerWidth < 768;
      const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobileScreen(isSmall || isMobileUA);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [keySearch, setKeySearch] = useState('');
  const [currentIntroVideo, setCurrentIntroVideo] = useState(() => {
    try {
      const saved = localStorage.getItem('garaoke_current_intro');
      if (saved) return saved;
    } catch (e) {}
    return process.env.VITE_DEFAULT_INTRO_VIDEO_CODE || process.env.REACT_APP_DEFAULT_INTRO_VIDEO_CODE || 'H_bB0sAqLNg';
  });
  const [currentTransitionVideo, setCurrentTransitionVideo] = useState(() => {
    try {
      const saved = localStorage.getItem('garaoke_current_transition');
      if (saved) return saved;
    } catch (e) {}
    return process.env.VITE_DEFAULT_TRANSITION_VIDEO_CODE || process.env.REACT_APP_DEFAULT_TRANSITION_VIDEO_CODE || 'wpXZZ4f_RKY';
  });
  const [videosSearchResponse, setVideosSearchResponse] = useState([]);
  const [previewVideId, setPreviewVideoId] = useState(null);
  const [isKaraokeOnly, setIsKaraokeOnly] = useState(true);
  const [playlist, setPlaylist] = useState(() => {
    try {
      const saved = localStorage.getItem('garaoke_user_playlist');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [currentPlaylist, setCurrentPlaylist] = useState(() => {
    try {
      const savedSelected = localStorage.getItem('garaoke_current_playlist');
      if (savedSelected) {
        return savedSelected;
      }
      const savedPlaylists = localStorage.getItem('garaoke_my_playlists');
      if (savedPlaylists) {
        const parsed = JSON.parse(savedPlaylists);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]?.id) {
          return parsed[0].id;
        }
      }
    } catch (e) {}
    return 'user_playlist';
  });
  const [message, setMessage] = useState({ text: '', type: '' });
  const [videoPlayer, setVideoPlayer] = useState(null);
  const [selectedApiKey, setSelectedApiKey] = useState(process.env.YOUTUBE_API_KEY || process.env.REACT_APP_DEFAULT_YOUTUBE_API_KEY);
  const [originPresetPlaylists, setOriginPresetPlaylists] = useState([]);
  const [originPresetPlaylist, setOriginPresetPlaylist] = useState([]);
  const [userPresetPlaylists, setUserPresetPlaylists] = useState(() => {
    try {
      const saved = localStorage.getItem('garaoke_preset_playlists');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [userPresetPlaylist, setUserPresetPlaylist] = useState([]);
  const [myPlaylists, setMyPlaylists] = useState(() => {
    try {
      const saved = localStorage.getItem('garaoke_my_playlists');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {
      console.warn('Error reading garaoke_my_playlists:', e);
    }
    try {
      const legacy = localStorage.getItem('garaoke_user_playlist');
      const items = legacy ? JSON.parse(legacy) : [];
      return [{ id: 'user_playlist', name: 'Mi lista', items: Array.isArray(items) ? items : [] }];
    } catch {
      return [{ id: 'user_playlist', name: 'Mi lista', items: [] }];
    }
  });

  const [screenSettings, setScreenSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('garaoke_screen_settings');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {}
    return { volume: 100, playbackRate: 1.0, pitch: 0 };
  });

  const [isIntegratedScreen, setIsIntegratedScreen] = useState(() => {
    try {
      const saved = localStorage.getItem('garaoke_screen_settings');
      if (saved) {
        const parsed = JSON.parse(saved);
        return !!parsed.isIntegrated;
      }
    } catch (e) {}
    return false;
  });

  const [isRemoteActive, setIsRemoteActive] = useState(false);
  const remoteLastSeenRef = useRef(0);

  // Chequeo periódico de presencia del control remoto
  useEffect(() => {
    const interval = setInterval(() => {
      if (remoteLastSeenRef.current && (Date.now() - remoteLastSeenRef.current > 35000)) {
        setIsRemoteActive(false);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const videoPlayerRef = useRef(videoPlayer);
  useEffect(() => {
    videoPlayerRef.current = videoPlayer;
  }, [videoPlayer]);

  // Cerrar ventana flotante inmediatamente si el usuario refresca o cierra la consola para no dejarla huérfana
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (videoPlayerRef.current && !videoPlayerRef.current.closed) {
        try {
          videoPlayerRef.current.close();
        } catch (e) {}
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
    };
  }, []);

  const handleToggleIntegratedScreen = useCallback((val) => {
    setIsIntegratedScreen((prev) => {
      const next = typeof val === 'boolean' ? val : !prev;
      setScreenSettings((s) => {
        const updated = { ...(s || {}), isIntegrated: next };
        try {
          localStorage.setItem('garaoke_screen_settings', JSON.stringify(updated));
        } catch (e) {}
        if (user?.uid) {
          updateDoc(doc(db, 'users', user.uid), {
            'configs.screenSettings.isIntegrated': next,
            'screenSettings.isIntegrated': next,
          }).catch(() => {});
        }
        return updated;
      });
      return next;
    });
  }, [user?.uid]);

  const [userSettings, setUserSettings] = useState(emptyUserSettings);
  const [activeOverlay, setActiveOverlay] = useState(null);
  const [screenMessage, setScreenMessage] = useState('');
  const [currentPlaylistItem, setCurrentPlaylistItem] = useState(null);
  const [allowInteractivity, setAllowInteractivity] = useState(true);
  const [showControlsPlayer, setShowControlsPlayer] = useState(true);
  const [videoId, setVideoId] = useState(null);

  const [continuousPlay, setContinuousPlay] = useState(() => {
    try {
      return localStorage.getItem('garaoke_continuous_play') === 'true';
    } catch (e) {
      return false;
    }
  });

  const [crossfadeEnabled, setCrossfadeEnabled] = useState(() => {
    try {
      return localStorage.getItem('garaoke_crossfade_enabled') === 'true';
    } catch (e) {
      return false;
    }
  });

  const [crossfadeDuration, setCrossfadeDuration] = useState(() => {
    try {
      const v = Number(localStorage.getItem('garaoke_crossfade_duration'));
      return !isNaN(v) && v >= 2 && v <= 15 ? v : (Number(import.meta.env?.VITE_DEFAULT_CROSSFADE_SEC) || 5);
    } catch (e) {
      return 5;
    }
  });

  const showModalQR = activeOverlay === 'qr';
  const showModalVotesTable = activeOverlay === 'votes';
  const showModalMessage = activeOverlay === 'message';

  const updateUserField = useCallback(async (field, value, uid = user?.uid) => {
    const targetUid = uid || user?.uid;
    if (!targetUid) return;
    let obj = {};
    obj[field] = value;
    try {
      const docRef = doc(db, 'users', targetUid);
      await updateDoc(docRef, { ...obj });
    } catch (error) {
      console.error('Error updating firebase: ', error);
    }
  }, [user?.uid]);

  // Persistencia de la última playlist seleccionada para que sobreviva a recargas F5 y sesiones
  useEffect(() => {
    if (currentPlaylist) {
      try {
        localStorage.setItem('garaoke_current_playlist', currentPlaylist);
      } catch (e) {}
      if (user?.uid) {
        updateUserField('currentPlaylist', currentPlaylist);
      }
    }
  }, [currentPlaylist, user?.uid, updateUserField]);

  // Persistencia del Host ID para sincronización de ventanas secundarias y monitores
  useEffect(() => {
    if (user?.uid) {
      try {
        localStorage.setItem('garaoke_host_id', user.uid);
      } catch (e) {}
    }
  }, [user?.uid]);

  /* eslint-disable */

  // Escuchar cuando el reproductor cierre cualquier overlay desde la cruz o se cierre la ventana
  useEffect(() => {
    const handlePlayerWindowMessage = (event) => {
      const data = event?.data;
      if (!data) return;
      if (data.type === 'overlayClosed') {
        setActiveOverlay(null);
        if (user?.uid) {
          try {
            const userRef = doc(db, 'users', user.uid);
            updateDoc(userRef, { activeOverlay: null }).catch(() => {});
          } catch (e) {}
        }
      } else if (data.type === 'playerClosed' || data.type === 'screenClosed') {
        setVideoPlayer(null);
        setVideoId(null);
        setCurrentPlaylistItem(null);
        if (user?.uid) {
          updateUserField('currentVideoData', JSON.stringify({ videoId: null, time: 0, assignedTo: null }), user.uid);
          updateUserField('currentPlaybackState', JSON.stringify({ status: 'STOPPED', videoId: null, time: 0 }), user.uid);
        }
      }
    };
    window.addEventListener('message', handlePlayerWindowMessage);
    let bc = null;
    try {
      if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
        bc = new BroadcastChannel('garaoke_player_sync');
        bc.onmessage = handlePlayerWindowMessage;
      }
    } catch (e) {}
    return () => {
      window.removeEventListener('message', handlePlayerWindowMessage);
      if (bc) bc.close();
    };
  }, [user?.uid, updateUserField]);

  const notifyPlayer = useCallback((payload) => {
    // 1. Notificar a ventana externa /player si está abierta
    if (videoPlayer && !videoPlayer.closed) {
      try {
        videoPlayer.postMessage(payload, '*');
        if (payload?.type === 'changeVideo' || payload?.type === 'playVideo' || payload?.type === 'triggerCrossfade' || payload?.type === 'START_CROSSFADE' || payload?.type === 'unpauseVideo') {
          try { videoPlayer.focus(); } catch (fErr) {}
        }
      } catch (e) {}
    }
    // 2. Notificar a la misma ventana (para modo pantalla integrada)
    try {
      if (typeof window !== 'undefined') {
        window.postMessage(payload, '*');
      }
    } catch (e) {}
    // 3. Notificar a través de BroadcastChannel para otras ventanas/pestañas del mismo origen
    try {
      if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
        const bc = new BroadcastChannel('garaoke_player_sync');
        bc.postMessage(payload);
        bc.close();
      }
    } catch (e) {}
  }, [videoPlayer]);

  // Daemon para detectar cuando la ventana externa del reproductor se cierra (incluso abruptamente)
  useEffect(() => {
    if (!videoPlayer || isIntegratedScreen) return;
    const interval = setInterval(() => {
      try {
        if (videoPlayer.closed) {
          setVideoPlayer(null);
          setVideoId(null);
          setCurrentPlaylistItem(null);
          notifyPlayer({ type: 'playerClosed' });
          if (user?.uid) {
            updateUserField('currentVideoData', JSON.stringify({ videoId: null, time: 0, assignedTo: null }), user.uid);
            updateUserField('currentPlaybackState', JSON.stringify({ status: 'STOPPED', videoId: null, time: 0 }), user.uid);
          }
        }
      } catch (e) {}
    }, 1000);
    return () => clearInterval(interval);
  }, [videoPlayer, isIntegratedScreen, user?.uid, updateUserField, notifyPlayer]);

  const saveUserSettings = useCallback(async (userId, settings) => {
    try {
      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, settings, { merge: true });
      setUserSettings((prev) => ({ ...prev, ...settings }));
    } catch (error) {
      setMessage({ text: `Error al guardar la configuración: ${JSON.stringify(error)}`, type: 'error' });
    }
  }, []);

  const getUserSettings = useCallback(async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnapshot = await getDoc(userRef);
      if (userSnapshot.exists()) {
        const settings = userSnapshot.data();
        let loadedMyPlaylists = settings.myPlaylists;
        if (!loadedMyPlaylists || !Array.isArray(loadedMyPlaylists) || loadedMyPlaylists.length === 0) {
          loadedMyPlaylists = [{ id: 'user_playlist', name: 'Mi lista', items: settings.playlist || [] }];
        }
        setMyPlaylists(loadedMyPlaylists);
        try {
          localStorage.setItem('garaoke_my_playlists', JSON.stringify(loadedMyPlaylists));
        } catch (e) {}

        const targetPlaylist = settings.currentPlaylist || (() => {
          try { return localStorage.getItem('garaoke_current_playlist'); } catch { return null; }
        })();
        if (targetPlaylist) {
          setCurrentPlaylist(targetPlaylist);
          try { localStorage.setItem('garaoke_current_playlist', targetPlaylist); } catch (e) {}
        } else {
          setCurrentPlaylist((prev) => {
            const exists = loadedMyPlaylists.some(p => p.id === prev || p.name === prev);
            if (exists) return prev;
            return loadedMyPlaylists[0]?.id || 'user_playlist';
          });
        }

        // Restaurar Intro seleccionada desde Firebase
        if (settings.currentIntroVideo) {
          setCurrentIntroVideo(settings.currentIntroVideo);
          try { localStorage.setItem('garaoke_current_intro', settings.currentIntroVideo); } catch (e) {}
        }

        // Restaurar Transición seleccionada desde Firebase
        if (settings.currentTransitionVideo) {
          setCurrentTransitionVideo(settings.currentTransitionVideo);
          try { localStorage.setItem('garaoke_current_transition', settings.currentTransitionVideo); } catch (e) {}
        }

        const loadedScreenSettings = settings.configs?.screenSettings || settings.screenSettings || {
          volume: 100,
          playbackRate: 1.0,
          pitch: 0,
          isIntegrated: false
        };
        setScreenSettings(loadedScreenSettings);
        if (loadedScreenSettings.isIntegrated !== undefined) {
          setIsIntegratedScreen(!!loadedScreenSettings.isIntegrated);
        }
        try {
          localStorage.setItem('garaoke_screen_settings', JSON.stringify(loadedScreenSettings));
        } catch (e) {}

        // Restaurar volumen de efectos de sonido
        if (typeof settings.effectsVolume === 'number') {
          try { localStorage.setItem('garaoke_effects_volume', String(settings.effectsVolume)); } catch (e) {}
          window.dispatchEvent(new CustomEvent('garaoke_set_effects_volume', {
            detail: { volume: settings.effectsVolume }
          }));
        }

        setUserSettings({
          configs: {
            general: {
              showControlsPlayer: settings.configs?.general?.showControlsPlayer ?? true,
              allowInteractivity: settings.configs?.general?.allowInteractivity ?? true,
              theme: settings.configs?.general?.theme === 'light' ? 'light' : 'dark',
            },
            screenSettings: loadedScreenSettings,
            apiKeys: settings.configs?.apiKeys ?? Array(5).fill(''),
            intros: settings.configs?.intros ?? Array(5).fill(''),
            transitions: settings.configs?.transitions ?? Array(5).fill(''),
          },
          playlist: settings.playlist ?? [],
          presetPlaylists: settings.presetPlaylists ?? [],
          myPlaylists: loadedMyPlaylists,
        });
        setPlaylist(settings.playlist ?? []);
        setUserPresetPlaylists(settings.presetPlaylists ?? []);
        setAllowInteractivity(settings.configs?.general?.allowInteractivity ?? true);
        setShowControlsPlayer(settings.configs?.general?.showControlsPlayer ?? true);

        if (settings.continuousPlay !== undefined) {
          setContinuousPlay(!!settings.continuousPlay);
          try { localStorage.setItem('garaoke_continuous_play', String(!!settings.continuousPlay)); } catch (e) {}
        }
        if (settings.crossfadeEnabled !== undefined) {
          setCrossfadeEnabled(!!settings.crossfadeEnabled);
          try { localStorage.setItem('garaoke_crossfade_enabled', String(!!settings.crossfadeEnabled)); } catch (e) {}
        }
        if (settings.crossfadeDuration !== undefined) {
          const cd = Number(settings.crossfadeDuration);
          if (!isNaN(cd) && cd >= 2 && cd <= 15) {
            setCrossfadeDuration(cd);
            try { localStorage.setItem('garaoke_crossfade_duration', String(cd)); } catch (e) {}
          }
        }

        if (settings.currentPlaybackState) {
          try {
            const parsed = typeof settings.currentPlaybackState === 'string'
              ? JSON.parse(settings.currentPlaybackState)
              : settings.currentPlaybackState;
            if (parsed?.videoId) {
              setVideoId(parsed.videoId);
            }
          } catch (e) {}
        }
      } else {
        setUserSettings(emptyUserSettings);
      }
    } catch (error) {
      console.warn('Configuración local dev o fallback:', error);
      setUserSettings(emptyUserSettings);
    }
  }, []);

  const handleUpdateScreenSettings = useCallback((newSettings, saveToDb = false) => {
    setScreenSettings(prev => {
      const merged = { ...prev, ...newSettings };

      // Notificar al reproductor (vía postMessage y BroadcastChannel)
      if (newSettings.volume !== undefined) {
        notifyPlayer({ type: 'setVolume', volume: newSettings.volume });
      }
      if (newSettings.playbackRate !== undefined) {
        notifyPlayer({ type: 'setPlaybackRate', playbackRate: newSettings.playbackRate });
      }
      if (newSettings.pitch !== undefined) {
        notifyPlayer({ type: 'setPitch', pitch: newSettings.pitch });
      }

      // Persistir localmente
      try {
        localStorage.setItem('garaoke_screen_settings', JSON.stringify(merged));
      } catch (e) {}

      // Persistir en Firestore si se requiere
      if (saveToDb) {
        const newUserSettings = {
          ...userSettings,
          configs: {
            ...userSettings.configs,
            screenSettings: merged,
          }
        };
        setUserSettings(newUserSettings);
        if (user && user.uid) {
          saveUserSettings(user.uid, newUserSettings);
          try {
            const userRef = doc(db, 'users', user.uid);
            updateDoc(userRef, {
              'configs.screenSettings.volume': merged.volume,
              screenVolume: merged.volume,
            }).catch(() => {});
          } catch (e) {}
        }
      }

      return merged;
    });
  }, [notifyPlayer, userSettings, user, saveUserSettings]);

  const handleResetScreenSettings = useCallback(() => {
    const defaultSettings = { volume: 100, playbackRate: 1.0, pitch: 0 };
    setScreenSettings(defaultSettings);

    notifyPlayer({ type: 'resetPlayerSettings' });

    try {
      localStorage.setItem('garaoke_screen_settings', JSON.stringify(defaultSettings));
    } catch (e) {}

    const newUserSettings = {
      ...userSettings,
      configs: {
        ...userSettings.configs,
        screenSettings: defaultSettings,
      }
    };
    setUserSettings(newUserSettings);
    if (user && user.uid) {
      saveUserSettings(user.uid, newUserSettings);
    }
  }, [notifyPlayer, userSettings, user, saveUserSettings]);

  const generateNewPresetPlaylists = useCallback((newPlaylist, currentPresets = []) => {
    const newPlaylistObj = {
      name: currentPlaylist,
      items: newPlaylist,
    };
    const presetsCopy = Array.isArray(currentPresets) ? [...currentPresets] : [];
    const matchIndex = presetsCopy.findIndex(p => p.name === currentPlaylist);
    if (matchIndex >= 0) {
      presetsCopy[matchIndex] = newPlaylistObj;
    } else {
      presetsCopy.push(newPlaylistObj);
    }
    return presetsCopy;
  }, [currentPlaylist]);

  const handleSaveUserPlaylist = useCallback((newPlaylist) => {
    const newUserSettings = { ...userSettings };
    
    // Verificar si currentPlaylist corresponde a una lista propia de myPlaylists
    const myPlaylistIndex = myPlaylists.findIndex(p => p.id === currentPlaylist || p.name === currentPlaylist);
    if (myPlaylistIndex >= 0) {
      const updatedMyPlaylists = [...myPlaylists];
      updatedMyPlaylists[myPlaylistIndex] = {
        ...updatedMyPlaylists[myPlaylistIndex],
        items: newPlaylist
      };
      setMyPlaylists(updatedMyPlaylists);
      setPlaylist(newPlaylist);

      newUserSettings.playlist = newPlaylist;
      newUserSettings.currentPlaylist = currentPlaylist;
      newUserSettings.myPlaylists = updatedMyPlaylists;

      try {
        localStorage.setItem('garaoke_my_playlists', JSON.stringify(updatedMyPlaylists));
        localStorage.setItem('garaoke_user_playlist', JSON.stringify(newPlaylist));
      } catch (e) {
        console.warn('Error saving to localStorage:', e);
      }
    } else {
      const updatedPresets = generateNewPresetPlaylists(newPlaylist, newUserSettings.presetPlaylists || []);
      newUserSettings.presetPlaylists = updatedPresets;
      newUserSettings.playlist = newPlaylist;
      newUserSettings.currentPlaylist = currentPlaylist;
      setUserPresetPlaylists(updatedPresets);
      setUserPresetPlaylist(newPlaylist);
      try {
        localStorage.setItem('garaoke_preset_playlists', JSON.stringify(updatedPresets));
      } catch (e) {
        console.warn('Error saving preset playlists to localStorage:', e);
      }
    }

    setUserSettings(newUserSettings);
    if (user && user.uid) {
      saveUserSettings(user.uid, newUserSettings);
    }
  }, [currentPlaylist, myPlaylists, generateNewPresetPlaylists, saveUserSettings, userSettings, user]);

  const handleCreatePlaylist = useCallback((name, initialItems = []) => {
    const cleanName = sanitizePlaylistName(name).trim() || 'Nueva lista';
    const newList = {
      id: `pl_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      name: cleanName,
      items: Array.isArray(initialItems) ? initialItems : [],
      createdAt: new Date().toISOString()
    };
    const updated = [...myPlaylists, newList];
    setMyPlaylists(updated);
    setCurrentPlaylist(newList.id);
    setPlaylist(newList.items);

    try {
      localStorage.setItem('garaoke_my_playlists', JSON.stringify(updated));
    } catch (e) {}

    const newUserSettings = { ...userSettings, myPlaylists: updated };
    setUserSettings(newUserSettings);
    if (user && user.uid) {
      saveUserSettings(user.uid, newUserSettings);
    }
    return newList;
  }, [myPlaylists, userSettings, user, saveUserSettings]);

  const handleAddToUserPlaylist = useCallback((video, targetPlaylistId = null, assignedTo = '') => {
    if (!video) return;

    // Determinar la lista destino
    let targetIndex = -1;
    let targetList = null;

    if (targetPlaylistId) {
      targetIndex = myPlaylists.findIndex(p => p.id === targetPlaylistId || p.name === targetPlaylistId);
    }

    // Fallback: si no se indicó ID o no se encontró, buscar la activa si es de usuario, o la primera de myPlaylists
    if (targetIndex === -1) {
      targetIndex = myPlaylists.findIndex(p => p.id === currentPlaylist || p.name === currentPlaylist);
    }
    if (targetIndex === -1 && myPlaylists.length > 0) {
      targetIndex = 0;
    }

    let updatedMyPlaylists = [...myPlaylists];

    // Si aún no hay ninguna lista propia creada, inicializar con "Mi lista"
    if (targetIndex === -1) {
      targetList = { id: 'user_playlist', name: 'Mi lista', items: [] };
      updatedMyPlaylists = [targetList];
      targetIndex = 0;
    } else {
      targetList = updatedMyPlaylists[targetIndex];
    }

    const currentItems = targetList.items || [];
    const lastAssignedId = currentItems.reduce((maxId, item) => {
      const currentId = parseInt(item.id, 10);
      return !isNaN(currentId) && currentId > maxId ? currentId : maxId;
    }, 0);

    const vidId = typeof video.id === 'string' ? video.id : (video.id?.videoId || video.videoId || null);
    const title = video.snippet?.title || video.title || 'Canción sin título';
    const thumbnail = video.snippet?.thumbnails?.high?.url || video.snippet?.thumbnails?.medium?.url || video.snippet?.thumbnails?.default?.url || video.thumbnail || '';

    const newItem = {
      id: (lastAssignedId + 1).toString(),
      title: title,
      videoId: vidId,
      thumbnail: thumbnail,
      played: false,
      assignedTo: (typeof assignedTo === 'string' ? assignedTo : '').trim(),
      votes: [],
    };

    const newItems = [...currentItems, newItem];
    const updatedTarget = { ...targetList, items: newItems };
    updatedMyPlaylists[targetIndex] = updatedTarget;

    setMyPlaylists(updatedMyPlaylists);
    setCurrentPlaylist(updatedTarget.id || updatedTarget.name);
    setPlaylist(newItems);

    try {
      localStorage.setItem('garaoke_my_playlists', JSON.stringify(updatedMyPlaylists));
      if (updatedTarget.id === 'user_playlist' || updatedTarget.name === 'Mi lista') {
        localStorage.setItem('garaoke_user_playlist', JSON.stringify(newItems));
      }
    } catch (e) {
      console.warn('Error saving to localStorage:', e);
    }

    const newUserSettings = { ...userSettings, myPlaylists: updatedMyPlaylists };
    if (updatedTarget.id === 'user_playlist' || updatedTarget.name === 'Mi lista') {
      newUserSettings.playlist = newItems;
    }
    setUserSettings(newUserSettings);
    if (user && user.uid) {
      saveUserSettings(user.uid, newUserSettings);
    }
  }, [myPlaylists, currentPlaylist, userSettings, user, saveUserSettings]);

  const handleRenamePlaylist = useCallback((idOrName, newName) => {
    const cleanName = sanitizePlaylistName(newName).trim();
    if (!cleanName) return;

    const updated = myPlaylists.map(p => {
      if (p.id === idOrName || p.name === idOrName) {
        return { ...p, name: cleanName };
      }
      return p;
    });

    setMyPlaylists(updated);
    if (currentPlaylist === idOrName) {
      const matched = updated.find(p => p.id === idOrName || p.name === cleanName);
      if (matched) {
        setCurrentPlaylist(matched.id || matched.name);
      }
    }

    try {
      localStorage.setItem('garaoke_my_playlists', JSON.stringify(updated));
    } catch (e) {}

    const newUserSettings = { ...userSettings, myPlaylists: updated };
    setUserSettings(newUserSettings);
    if (user && user.uid) {
      saveUserSettings(user.uid, newUserSettings);
    }
  }, [myPlaylists, currentPlaylist, userSettings, user, saveUserSettings]);

  const handleDeletePlaylist = useCallback((idOrName) => {
    let updated = myPlaylists.filter(p => p.id !== idOrName && p.name !== idOrName);
    
    // Si se eliminaron todas las listas propias, garantizar que siempre exista "Mi lista" vacía
    if (updated.length === 0) {
      updated = [{ id: 'user_playlist', name: 'Mi lista', items: [] }];
    }

    setMyPlaylists(updated);

    // Si la lista que se eliminó estaba activa en pantalla, cambiar a la primera disponible
    if (currentPlaylist === idOrName) {
      const nextActive = updated[0];
      setCurrentPlaylist(nextActive.id || nextActive.name);
      setPlaylist(nextActive.items || []);
    }

    try {
      localStorage.setItem('garaoke_my_playlists', JSON.stringify(updated));
    } catch (e) {}

    const newUserSettings = { ...userSettings, myPlaylists: updated };
    setUserSettings(newUserSettings);
    if (user && user.uid) {
      saveUserSettings(user.uid, newUserSettings);
    }
  }, [myPlaylists, currentPlaylist, userSettings, user, saveUserSettings]);

  const fullReset = () => {
    setKeySearch('');
    setCurrentIntroVideo(process.env.VITE_DEFAULT_INTRO_VIDEO_CODE || process.env.REACT_APP_DEFAULT_INTRO_VIDEO_CODE);
    setCurrentTransitionVideo(process.env.VITE_DEFAULT_TRANSITION_VIDEO_CODE || process.env.REACT_APP_DEFAULT_TRANSITION_VIDEO_CODE);
    setVideosSearchResponse([]);
    setPreviewVideoId(null);
    setIsKaraokeOnly(true);
    setPlaylist([]);
    setCurrentPlaylist('user_playlist');
    setVideoPlayer(null);
    setSelectedApiKey(process.env.YOUTUBE_API_KEY || process.env.REACT_APP_DEFAULT_YOUTUBE_API_KEY);
    setOriginPresetPlaylists([]);
    setOriginPresetPlaylist([]);
    setUserPresetPlaylists([]);
    setUserPresetPlaylist([]);
    setUserSettings(emptyUserSettings);
    setAllowInteractivity(true);
  };

  const openVideoPlayer = useCallback((vid = currentTransitionVideo, forceFloating = false, isPlaylist = false, type = null, startTime = 0, endTime = 0) => {
    const defaultTransition = process.env.VITE_DEFAULT_TRANSITION_VIDEO_CODE || process.env.REACT_APP_DEFAULT_TRANSITION_VIDEO_CODE || 'wpXZZ4f_RKY';
    const targetVid = (vid && vid !== 'undefined') ? vid : (currentTransitionVideo || defaultTransition);
    const prevVid = videoId && videoId !== targetVid ? videoId : null;
    setVideoId(targetVid);

    const defaultTrans = process.env.VITE_DEFAULT_TRANSITION_VIDEO_CODE || process.env.REACT_APP_DEFAULT_TRANSITION_VIDEO_CODE || 'wpXZZ4f_RKY';
    const isSpecial = targetVid === currentTransitionVideo || targetVid === defaultTrans;

    let matchedItem = Array.isArray(playlist) ? playlist.find(p => p.videoId === targetVid) : null;
    if (!matchedItem) {
      const snap = getActivePlaylistSnapshot();
      if (snap && Array.isArray(snap.items)) {
        matchedItem = snap.items.find(p => p.videoId === targetVid);
      }
    }
    if (!matchedItem && Array.isArray(myPlaylists)) {
      for (const pl of myPlaylists) {
        if (Array.isArray(pl.items)) {
          const it = pl.items.find(p => p.videoId === targetVid);
          if (it) { matchedItem = it; break; }
        }
      }
    }

    const songTitle = matchedItem?.title || '';
    const songAssignedTo = matchedItem?.assignedTo || '';
    const songStartTime = (startTime !== undefined && startTime !== null && !isNaN(Number(startTime)) && Number(startTime) > 0)
      ? Number(startTime)
      : Math.max(0, Number(matchedItem?.startTime ?? matchedItem?.t ?? 0) || 0);
    const songEndTime = (endTime !== undefined && endTime !== null && !isNaN(Number(endTime)) && Number(endTime) > 0)
      ? Number(endTime)
      : Math.max(0, Number(matchedItem?.endTime ?? 0) || 0);

    const newItem = {
      videoId: targetVid,
      title: songTitle,
      assignedTo: songAssignedTo,
      startTime: songStartTime,
      endTime: songEndTime,
      votes: matchedItem?.votes || []
    };

    setCurrentPlaylistItem({
      playlist: currentPlaylist,
      item: newItem
    });

    const changeVideoPayload = {
      type: 'changeVideo',
      videoId: targetVid,
      previousVideoId: isSpecial ? null : prevVid,
      title: songTitle,
      assignedTo: songAssignedTo,
      startTime: isSpecial ? 0 : songStartTime,
      endTime: isSpecial ? 0 : songEndTime,
      playlist: currentPlaylist,
    };

    // Notificar inmediatamente tanto por SSE como por Firestore para que /remote y los móviles se sincronicen (<20ms)
    if (user?.uid && targetVid) {
      const stateObj = {
        videoId: isSpecial ? null : targetVid,
        status: 'PLAYING',
        baseTime: isSpecial ? 0 : songStartTime,
        endTime: isSpecial ? 0 : songEndTime,
        startedAt: Date.now(),
        playbackRate: screenSettings?.playbackRate ?? 1.0,
        assignedTo: songAssignedTo,
        title: songTitle,
      };
      // 1. Sincronización en la nube con Firestore (Siempre activa, compatible con Netlify)
      try {
        const userRef = doc(db, 'users', user.uid);
        updateDoc(userRef, {
          currentPlaybackState: stateObj,
          currentVideoData: JSON.stringify({
            videoId: isSpecial ? null : targetVid,
            time: isSpecial ? 0 : songStartTime,
            endTime: isSpecial ? 0 : songEndTime,
            assignedTo: songAssignedTo,
            title: songTitle,
          }),
        }).catch(() => {});
      } catch (e) {}

      // 2. Notificación a backend Express SSE si está activo (red local o Render)
      if (isSyncBackendAvailable) {
        try {
          fetch(`${apiBaseUrl}/sync/state/${user.uid}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(stateObj),
          }).catch(() => {});

          fetch(`${apiBaseUrl}/sync/command`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              hostId: user.uid,
              action: 'SONG_STARTED',
              payload: { videoId: isSpecial ? null : targetVid, startTime: isSpecial ? 0 : songStartTime, endTime: isSpecial ? 0 : songEndTime },
            }),
          }).catch(() => {});
        } catch (e) {}
      }
    }

    if (forceFloating) {
      if (isIntegratedScreen) {
        handleToggleIntegratedScreen(false);
      }
    } else if (isIntegratedScreen) {
      notifyPlayer(changeVideoPayload);
      return;
    }

    const vol = screenSettings?.volume ?? 100;
    const rate = screenSettings?.playbackRate ?? 1.0;
    const pitch = screenSettings?.pitch ?? 0;
    const query = `/player?videoId=${targetVid}&userId=${user?.uid || ''}&volume=${vol}&rate=${rate}&pitch=${pitch}&startTime=${isSpecial ? 0 : songStartTime}&endTime=${isSpecial ? 0 : songEndTime}`;

    if (videoPlayer && !videoPlayer.closed && videoPlayer.name) {
      try {
        notifyPlayer(changeVideoPayload);
        videoPlayer.focus();
      } catch (e) {}
    } else {
      setVideoPlayer(window.open(query, 'videoPlayer'));
    }
  }, [videoPlayer, user, currentTransitionVideo, screenSettings, isIntegratedScreen, notifyPlayer, handleToggleIntegratedScreen, playlist, myPlaylists, videoId, currentPlaylist]);

  const toggleOverlay = useCallback((overlayName, payload = {}) => {
    setActiveOverlay((prev) => {
      const next = prev === overlayName ? null : overlayName;
      const overlayMsg = {
        type: 'setOverlay',
        overlay: next,
        message: payload.message !== undefined ? payload.message : screenMessage,
        query: '?UID=' + (user?.uid || ''),
      };
      if (videoPlayer && !videoPlayer.closed) {
        if (!next) {
          videoPlayer.postMessage({ type: 'setOverlay', overlay: null }, '*');
        } else {
          videoPlayer.postMessage(overlayMsg, '*');
        }
      }
      notifyPlayer(next ? overlayMsg : { type: 'setOverlay', overlay: null });

      // Sincronizar con Firestore para que el móvil (/remote) conozca si el overlay está activo o apagado
      if (user?.uid) {
        try {
          const userRef = doc(db, 'users', user.uid);
          updateDoc(userRef, { activeOverlay: next }).catch(() => {});
        } catch (e) {}
      }

      return next;
    });
  }, [videoPlayer, screenMessage, user, notifyPlayer]);

  const showHideModalQR = useCallback(() => {
    toggleOverlay('qr');
  }, [toggleOverlay]);

  const showHideModalVotesTable = useCallback(() => {
    toggleOverlay('votes');
  }, [toggleOverlay]);

  const triggerManualVotation = useCallback(async () => {
    if (!user?.uid) return;
    const targetVideoId = currentPlaylistItem?.item?.videoId || currentPlaylistItem?.videoId || videoId;
    
    let targetAssignedTo = currentPlaylistItem?.item?.assignedTo || currentPlaylistItem?.assignedTo || '';
    if (!targetAssignedTo && targetVideoId && Array.isArray(playlist)) {
      const found = playlist.find(it => (it.videoId === targetVideoId || it.id === targetVideoId || it.item?.videoId === targetVideoId));
      targetAssignedTo = found?.assignedTo || found?.item?.assignedTo || '';
    }
    if (!targetAssignedTo && userSettings?.currentVideoData) {
      try {
        const cvd = typeof userSettings.currentVideoData === 'string' ? JSON.parse(userSettings.currentVideoData) : userSettings.currentVideoData;
        if (cvd?.assignedTo) targetAssignedTo = cvd.assignedTo;
      } catch (e) {}
    }

    const targetTitle = currentPlaylistItem?.item?.title || currentPlaylistItem?.title || '';

    if (!targetAssignedTo || !targetAssignedTo.trim()) {
      setMessage({ text: 'No hay un cantante asignado en el track actual para votar', type: 'error' });
      return;
    }

    const triggerData = {
      timestamp: Date.now(),
      triggerId: `${Date.now()}_${Math.random().toString(36).substring(7)}`,
      videoId: targetVideoId || '',
      assignedTo: targetAssignedTo.trim(),
      title: targetTitle,
    };

    try {
      await updateDoc(doc(db, 'users', user.uid), {
        manualVotationTrigger: triggerData
      });
      notifyPlayer({ type: 'TRIGGER_MANUAL_VOTATION', ...triggerData });
      
      try {
        const bc = new BroadcastChannel('garaoke_player_sync');
        bc.postMessage({ type: 'TRIGGER_MANUAL_VOTATION', ...triggerData });
        bc.close();
      } catch (e) {}

      window.dispatchEvent(new CustomEvent('garaoke_manual_votation', { detail: triggerData }));
    } catch (err) {
      console.error('Error lanzando votación manual:', err);
      setMessage({ text: 'Error al lanzar votación a los monitores', type: 'error' });
    }
  }, [user?.uid, currentPlaylistItem, videoId, playlist, userSettings, notifyPlayer]);

  // Contador de votos en tiempo real en subcolección 'votes'
  const [subcollectionVotesCount, setSubcollectionVotesCount] = useState(0);

  useEffect(() => {
    if (!user?.uid) {
      setSubcollectionVotesCount(0);
      return;
    }
    let unsubscribe = () => {};
    try {
      const votesColRef = collection(db, 'users', user.uid, 'votes');
      unsubscribe = onSnapshot(votesColRef, (snapshot) => {
        setSubcollectionVotesCount(snapshot.size);
      }, (err) => {
        console.warn('Error escuchando subcolección votes en App:', err);
      });
    } catch (e) {}

    return () => unsubscribe();
  }, [user?.uid]);

  const playlistHasVotes = Array.isArray(playlist) && playlist.some(it => Array.isArray(it.votes) && it.votes.length > 0);
  const presetHasVotes = Array.isArray(userPresetPlaylists) && userPresetPlaylists.some(pl => Array.isArray(pl.items) && pl.items.some(it => Array.isArray(it.votes) && it.votes.length > 0));
  const hasVotes = subcollectionVotesCount > 0 || playlistHasVotes || presetHasVotes;

  // Reiniciar todas las votaciones (eliminar subcolección y limpiar arrays)
  const handleResetVotes = useCallback(async () => {
    const targetUid = user?.uid;
    if (!targetUid) return;

    try {
      // 1. Eliminar documentos en subcolección 'votes'
      const votesColRef = collection(db, 'users', targetUid, 'votes');
      const snap = await getDocs(votesColRef);
      if (!snap.empty) {
        const batch = writeBatch(db);
        snap.forEach((d) => {
          batch.delete(d.ref);
        });
        await batch.commit();
      }

      // 2. Limpiar votes en playlist actual del usuario
      if (Array.isArray(playlist) && playlist.some(it => it.votes?.length > 0)) {
        const cleanedPlaylist = playlist.map(it => ({ ...it, votes: [] }));
        setPlaylist(cleanedPlaylist);
        await updateUserField('playlist', cleanedPlaylist, targetUid);
      }

      // 3. Limpiar votes en presetPlaylists si aplica
      if (Array.isArray(userPresetPlaylists) && userPresetPlaylists.some(pl => pl.items?.some(it => it.votes?.length > 0))) {
        const cleanedPresets = userPresetPlaylists.map(pl => ({
          ...pl,
          items: (pl.items || []).map(it => ({ ...it, votes: [] }))
        }));
        setUserPresetPlaylists(cleanedPresets);
        await updateUserField('presetPlaylists', cleanedPresets, targetUid);
      }

      // 4. Notificar vía BroadcastChannel
      try {
        if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
          const bc = new BroadcastChannel('garaoke_player_sync');
          bc.postMessage({ type: 'VOTES_RESET', timestamp: Date.now() });
          bc.close();
        }
      } catch (e) {}

      // 5. Notificar a reproductor externo
      notifyPlayer({ type: 'VOTES_RESET' });

      // 6. Notificar al backend Express SSE si está disponible
      if (isSyncBackendAvailable) {
        fetch(`${apiBaseUrl}/sync/votes/${targetUid}/reset`, { method: 'POST' }).catch(() => {});
      }
    } catch (err) {
      console.error('Error reiniciando votaciones:', err);
      setMessage({
        text: 'Error al reiniciar las votaciones.',
        type: 'error',
        duration: 4000,
      });
    }
  }, [user?.uid, playlist, userPresetPlaylists, updateUserField, notifyPlayer]);
  
  const showHideModalMessage = useCallback((msg) => {
    if (msg !== undefined) {
      setScreenMessage(msg);
    }
    toggleOverlay('message', { message: msg });
  }, [toggleOverlay]);

  const changeIntro = useCallback((introId) => {
    setCurrentIntroVideo(introId);
    try {
      localStorage.setItem('garaoke_current_intro', introId);
    } catch (e) {}
    if (user?.uid) {
      updateDoc(doc(db, 'users', user.uid), { currentIntroVideo: introId }).catch(() => {});
    }
    notifyPlayer({ type: 'changeIntro', introId: introId });
    if (videoPlayer && !videoPlayer.closed) {
      try {
        videoPlayer.postMessage({ type: 'changeIntro', introId: introId }, '*');
      } catch (e) {}
    }
  }, [user?.uid, videoPlayer, notifyPlayer]);

  const changeTransition = useCallback((transitionId) => {
    setCurrentTransitionVideo(transitionId);
    try {
      localStorage.setItem('garaoke_current_transition', transitionId);
    } catch (e) {}
    if (user?.uid) {
      updateDoc(doc(db, 'users', user.uid), { currentTransitionVideo: transitionId }).catch(() => {});
    }
    notifyPlayer({ type: 'changeTransition', transitionId: transitionId });
    if (videoPlayer && !videoPlayer.closed) {
      try {
        videoPlayer.postMessage({ type: 'changeTransition', transitionId: transitionId }, '*');
      } catch (e) {}
    }
  }, [user?.uid, videoPlayer, notifyPlayer]);

  const changePlaylistItem = useCallback((playlistItem) => {
    setCurrentPlaylistItem(playlistItem);
    notifyPlayer({ type: 'changePlaylistItem', playlistItem: playlistItem });
    if (videoPlayer && !videoPlayer.closed) {
      try {
        videoPlayer.postMessage({ type: 'changePlaylistItem', playlistItem: playlistItem }, '*');
      } catch (e) {}
    }
  }, [videoPlayer, notifyPlayer]);

  const handleToggleContinuousPlay = useCallback((val) => {
    const nextVal = typeof val === 'boolean' ? val : !continuousPlay;
    setContinuousPlay(nextVal);
    try {
      localStorage.setItem('garaoke_continuous_play', String(nextVal));
    } catch (e) {}
    if (user?.uid) {
      updateDoc(doc(db, 'users', user.uid), { continuousPlay: nextVal }).catch(() => {});
    }
    notifyPlayer({ type: 'setContinuousPlay', continuousPlay: nextVal });
    if (videoPlayer && !videoPlayer.closed) {
      try {
        videoPlayer.postMessage({ type: 'setContinuousPlay', continuousPlay: nextVal }, '*');
      } catch (e) {}
    }
  }, [continuousPlay, user?.uid, videoPlayer, notifyPlayer]);

  const handleToggleCrossfade = useCallback((val) => {
    const nextVal = typeof val === 'boolean' ? val : !crossfadeEnabled;
    setCrossfadeEnabled(nextVal);
    try {
      localStorage.setItem('garaoke_crossfade_enabled', String(nextVal));
    } catch (e) {}
    if (user?.uid) {
      updateDoc(doc(db, 'users', user.uid), { crossfadeEnabled: nextVal }).catch(() => {});
    }
    notifyPlayer({ type: 'setCrossfadeEnabled', crossfadeEnabled: nextVal });
    if (videoPlayer && !videoPlayer.closed) {
      try {
        videoPlayer.postMessage({ type: 'setCrossfadeEnabled', crossfadeEnabled: nextVal }, '*');
      } catch (e) {}
    }
  }, [crossfadeEnabled, user?.uid, videoPlayer, notifyPlayer]);

  const handleUpdateCrossfadeDuration = useCallback((val) => {
    const nextVal = Math.max(2, Math.min(15, Number(val) || 5));
    setCrossfadeDuration(nextVal);
    try {
      localStorage.setItem('garaoke_crossfade_duration', String(nextVal));
    } catch (e) {}
    if (user?.uid) {
      updateDoc(doc(db, 'users', user.uid), { crossfadeDuration: nextVal }).catch(() => {});
    }
    notifyPlayer({ type: 'setCrossfadeDuration', crossfadeDuration: nextVal });
    if (videoPlayer && !videoPlayer.closed) {
      try {
        videoPlayer.postMessage({ type: 'setCrossfadeDuration', crossfadeDuration: nextVal }, '*');
      } catch (e) {}
    }
  }, [user?.uid, videoPlayer, notifyPlayer]);

  const activePlayerDeckRef = useRef('A');
  const crossfadeTriggeredForVidRef = useRef(null);

  const handlePlayNextSong = useCallback((fromVidId, isBroken = false) => {
    const snapshot = getActivePlaylistSnapshot();
    const activeItems = (snapshot && Array.isArray(snapshot.items) && snapshot.items.length > 0)
      ? snapshot.items
      : (() => {
        const myPl = myPlaylists.find(pl => pl.id === currentPlaylist || pl.name === currentPlaylist);
        if (myPl && Array.isArray(myPl.items) && myPl.items.length > 0) return myPl.items;
        if (Array.isArray(playlist) && playlist.length > 0) return playlist;
        const userPreset = userPresetPlaylists.find(pl => pl.name === currentPlaylist);
        if (userPreset && Array.isArray(userPreset.items) && userPreset.items.length > 0) return userPreset.items;
        const origPreset = originPresetPlaylists.find(pl => pl.name === currentPlaylist);
        if (origPreset && Array.isArray(origPreset.items) && origPreset.items.length > 0) return origPreset.items;
        return [];
      })();

    const targetPlaylist = snapshot?.playlistId || currentPlaylist;

    if (isBroken) {
      setMessage({
        text: 'Pista no disponible en YouTube. Saltando a la siguiente...',
        type: 'error',
        duration: 4000
      });
    }

    const targetVid = fromVidId || videoId;
    const { nextSong, isEndOfPlaylist } = resolveNextTrack(activeItems, targetVid, currentPlaylistItem, targetPlaylist);

    if (nextSong && nextSong.videoId) {
      const foundItem = Array.isArray(activeItems) ? activeItems.find(it => (it.videoId === nextSong.videoId || it.id === nextSong.videoId || it.id === nextSong.item?.id)) : null;
      const nextStartTime = Math.max(0, Number(nextSong.startTime ?? nextSong.item?.startTime ?? foundItem?.startTime ?? foundItem?.t ?? 0) || 0);
      const nextEndTime = Math.max(0, Number(nextSong.endTime ?? nextSong.item?.endTime ?? foundItem?.endTime ?? 0) || 0);
      const newPlaylistItem = { 
        playlist: targetPlaylist, 
        item: {
          ...(nextSong.item || nextSong),
          startTime: nextStartTime,
          endTime: nextEndTime
        } 
      };
      changePlaylistItem(newPlaylistItem);
      setVideoId(nextSong.videoId);
      openVideoPlayer(nextSong.videoId, false, true, null, nextStartTime, nextEndTime);
    } else {
      const defTrans = process.env.VITE_DEFAULT_TRANSITION_VIDEO_CODE || process.env.REACT_APP_DEFAULT_TRANSITION_VIDEO_CODE || 'wpXZZ4f_RKY';
      openVideoPlayer(currentTransitionVideo || defTrans);
    }
  }, [myPlaylists, currentPlaylist, playlist, userPresetPlaylists, originPresetPlaylists, videoId, currentPlaylistItem, changePlaylistItem, openVideoPlayer, currentTransitionVideo, setMessage]);

  // Consola dispara y comanda el inicio de Crossfade hacia el Deck entrante del Player
  const handleTriggerCrossfadeFromConsole = useCallback((fromVidId) => {
    const snapshot = getActivePlaylistSnapshot();
    const activeItems = (snapshot && Array.isArray(snapshot.items) && snapshot.items.length > 0)
      ? snapshot.items
      : (() => {
        const myPl = myPlaylists.find(pl => pl.id === currentPlaylist || pl.name === currentPlaylist);
        if (myPl && Array.isArray(myPl.items) && myPl.items.length > 0) return myPl.items;
        if (Array.isArray(playlist) && playlist.length > 0) return playlist;
        const userPreset = userPresetPlaylists.find(pl => pl.name === currentPlaylist);
        if (userPreset && Array.isArray(userPreset.items) && userPreset.items.length > 0) return userPreset.items;
        const origPreset = originPresetPlaylists.find(pl => pl.name === currentPlaylist);
        if (origPreset && Array.isArray(origPreset.items) && origPreset.items.length > 0) return origPreset.items;
        return [];
      })();

    const targetPlaylist = snapshot?.playlistId || currentPlaylist;
    const targetVid = fromVidId || videoId;
    const { nextSong, isEndOfPlaylist } = resolveNextTrack(activeItems, targetVid, currentPlaylistItem, targetPlaylist);

    // Determinar a qué Deck debe enviarse la siguiente canción (alternancia estricta comandada por la consola)
    const targetDeck = activePlayerDeckRef.current === 'A' ? 'B' : 'A';

    if (nextSong && nextSong.videoId) {
      const foundItem = Array.isArray(activeItems) ? activeItems.find(it => (it.videoId === nextSong.videoId || it.id === nextSong.videoId || it.id === nextSong.item?.id)) : null;
      const nextStartTime = Math.max(0, Number(nextSong.startTime ?? nextSong.item?.startTime ?? foundItem?.startTime ?? foundItem?.t ?? 0) || 0);
      const nextEndTime = Math.max(0, Number(nextSong.endTime ?? nextSong.item?.endTime ?? foundItem?.endTime ?? 0) || 0);

      const newPlaylistItem = {
        playlist: targetPlaylist,
        item: {
          ...(nextSong.item || nextSong),
          startTime: nextStartTime,
          endTime: nextEndTime
        }
      };

      console.log(`🎛️ [Consola] Disparando Crossfade hacia Deck ${targetDeck}: "${nextSong.title || nextSong.videoId}" (Start: ${nextStartTime}s, End: ${nextEndTime}s)`);

      notifyPlayer({
        type: 'START_CROSSFADE',
        incomingVid: nextSong.videoId,
        targetDeck: targetDeck,
        startTime: nextStartTime,
        endTime: nextEndTime,
        title: nextSong.title || '',
        assignedTo: nextSong.assignedTo || '',
        crossfadeDuration: crossfadeDuration || 5,
        nextSong: newPlaylistItem.item,
        playlist: targetPlaylist,
      });
    } else if (isEndOfPlaylist && currentTransitionVideo) {
      console.log(`🎛️ [Consola] Playlist finalizada: Disparando Crossfade hacia Deck ${targetDeck} con transición`);
      notifyPlayer({
        type: 'START_CROSSFADE',
        incomingVid: currentTransitionVideo,
        targetDeck: targetDeck,
        startTime: 0,
        endTime: 0,
        title: 'Cortina de transición',
        assignedTo: '',
        crossfadeDuration: crossfadeDuration || 5,
        nextSong: null,
        playlist: '',
      });
    }
  }, [myPlaylists, currentPlaylist, playlist, userPresetPlaylists, originPresetPlaylists, videoId, currentPlaylistItem, crossfadeDuration, currentTransitionVideo, notifyPlayer]);

  // Monitoreo de progreso en la Consola para disparar Crossfade en el segundo exacto
  useEffect(() => {
    if (!continuousPlay || !crossfadeEnabled) return;

    const handleProgress = (event) => {
      const data = event?.detail || event?.data;
      if (!data) return;
      const { videoId: progVidId, currentTime, effectiveDuration } = data;
      if (!progVidId || !currentTime || !effectiveDuration) return;

      const defTrans = process.env.VITE_DEFAULT_TRANSITION_VIDEO_CODE || process.env.REACT_APP_DEFAULT_TRANSITION_VIDEO_CODE || 'wpXZZ4f_RKY';
      if (progVidId === currentTransitionVideo || progVidId === defTrans || progVidId === currentIntroVideo) return;

      const remaining = effectiveDuration - currentTime;
      const xDuration = crossfadeDuration || 5;

      if (remaining <= xDuration && remaining > 0.3 && crossfadeTriggeredForVidRef.current !== progVidId) {
        crossfadeTriggeredForVidRef.current = progVidId;
        handleTriggerCrossfadeFromConsole(progVidId);
      }
    };

    window.addEventListener('garaoke_playback_progress', handleProgress);
    let bc = null;
    try {
      if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
        bc = new BroadcastChannel('garaoke_player_sync');
        bc.onmessage = (e) => {
          if (e.data?.type === 'playbackProgress') {
            handleProgress({ detail: e.data });
          }
        };
      }
    } catch (e) {}

    return () => {
      window.removeEventListener('garaoke_playback_progress', handleProgress);
      if (bc) bc.close();
    };
  }, [continuousPlay, crossfadeEnabled, crossfadeDuration, currentTransitionVideo, currentIntroVideo, handleTriggerCrossfadeFromConsole]);

  // Escuchar eventos de terminación, cambio de deck o error desde el reproductor
  useEffect(() => {
    const handlePlayerNotification = (event) => {
      const data = event?.data;
      if (!data) return;

      if (data.type === 'onDeckChanged' || data.type === 'onMidpointCrossfade') {
        if (data.activeDeck) {
          activePlayerDeckRef.current = data.activeDeck;
        }
      }

      if (data.type === 'onSongEnded') {
        if (continuousPlay && !crossfadeEnabled) {
          handlePlayNextSong(data.videoId, false);
        }
      } else if (data.type === 'onSongError') {
        if (continuousPlay) {
          handlePlayNextSong(data.videoId, true);
        } else {
          setMessage({
            text: 'Pista no disponible en YouTube (restringida o eliminada).',
            type: 'error',
            duration: 5000
          });
        }
      } else if (data.type === 'changeVideo' || data.type === 'onMidpointCrossfade') {
        if (data.videoId) {
          const defaultTrans = process.env.VITE_DEFAULT_TRANSITION_VIDEO_CODE || process.env.REACT_APP_DEFAULT_TRANSITION_VIDEO_CODE || 'wpXZZ4f_RKY';
          const isSpecial = data.videoId === currentTransitionVideo || data.videoId === defaultTrans || data.videoId === currentIntroVideo;
          setVideoId(data.videoId);

          const songTitle = data.title || data.nextSong?.title || '';
          const songAssignedTo = data.assignedTo || data.nextSong?.assignedTo || '';
          const songPlaylist = data.playlist || data.nextSong?.playlist || currentPlaylist;

          const newItem = data.nextSong?.item || {
            videoId: data.videoId,
            title: songTitle,
            assignedTo: songAssignedTo,
            votes: []
          };

          setCurrentPlaylistItem({
            playlist: songPlaylist,
            item: newItem
          });

          // Notificar a Firestore y SSE para sincronización en vivo con /remote y /monitor
          if (user?.uid && !isSpecial) {
            const stateObj = {
              videoId: data.videoId,
              status: 'PLAYING',
              baseTime: 0,
              startedAt: Date.now(),
              playbackRate: screenSettings?.playbackRate ?? 1.0,
              assignedTo: songAssignedTo,
              title: songTitle,
            };
            try {
              const userRef = doc(db, 'users', user.uid);
              updateDoc(userRef, {
                currentPlaybackState: stateObj,
                currentVideoData: JSON.stringify({
                  videoId: data.videoId,
                  time: 0,
                  assignedTo: songAssignedTo,
                  title: songTitle,
                }),
              }).catch(() => {});
            } catch (e) {}

            if (isSyncBackendAvailable) {
              try {
                fetch(`${apiBaseUrl}/sync/command`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    hostId: user.uid,
                    action: 'SONG_STARTED',
                    payload: { videoId: data.videoId, title: songTitle, assignedTo: songAssignedTo },
                  }),
                }).catch(() => {});
              } catch (e) {}
            }
          }
        }
      }
    };

    let channel = null;
    try {
      if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
        channel = new BroadcastChannel('garaoke_player_sync');
        channel.onmessage = handlePlayerNotification;
      }
    } catch (e) {}

    window.addEventListener('message', handlePlayerNotification);
    return () => {
      window.removeEventListener('message', handlePlayerNotification);
      if (channel) channel.close();
    };
  }, [continuousPlay, crossfadeEnabled, handlePlayNextSong, setMessage, currentTransitionVideo, currentIntroVideo, currentPlaylist, screenSettings, user?.uid]);

  // Set y Refs para deduplicación estricta de comandos remotos (elimina doble ejecución SSE + Firestore)
  const processedCommandIdsRef = useRef(new Set());
  const lastPlayedEffectRef = useRef({ id: null, path: null, timestamp: 0 });

  const shouldExecuteCommand = useCallback((cmdId, timestamp) => {
    if (!cmdId) return false;
    if (processedCommandIdsRef.current.has(cmdId)) {
      return false;
    }
    if (timestamp && Date.now() - timestamp > 25000) {
      return false;
    }
    processedCommandIdsRef.current.add(cmdId);
    if (processedCommandIdsRef.current.size > 200) {
      const arr = Array.from(processedCommandIdsRef.current);
      processedCommandIdsRef.current = new Set(arr.slice(arr.length - 100));
    }
    return true;
  }, []);

  // ── Receptor de Comandos en Tiempo Real desde el Control Remoto Móvil ──
  useEffect(() => {
    if (!user?.uid) return;
    // Si la ruta actual es /remote o /monitor, NO escuchar comandos remotos dirigidos a la Desktop
    if (location.pathname.startsWith('/remote') || location.pathname.startsWith('/monitor')) {
      return;
    }

    // Manejador centralizado de comandos remotos (reutilizado tanto por Firestore como por SSE)
    const handleExecuteCommand = (action, payload) => {
      console.log('📱 [Control Remoto] Ejecutando comando:', action, payload);

      switch (action) {
        case 'PLAY_SONG':
          if (payload?.videoId) {
            setVideoId(payload.videoId);
            const songStart = Math.max(0, Number(payload.startTime ?? payload.item?.startTime ?? payload.item?.t ?? 0) || 0);
            const songEnd = Math.max(0, Number(payload.endTime ?? payload.item?.endTime ?? 0) || 0);
            openVideoPlayer(payload.videoId, false, false, null, songStart, songEnd);
            if (payload.item) {
              changePlaylistItem({
                ...payload.item,
                startTime: songStart,
                endTime: songEnd,
              });
            }
            // Notificar estado a backend si existe y a Firestore
            try {
              fetch(`${apiBaseUrl}/sync/state/${user.uid}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  videoId: payload.videoId,
                  status: 'PLAYING',
                  startTime: songStart,
                  endTime: songEnd,
                  title: payload.title || payload.item?.title || '',
                  startedAt: Date.now(),
                }),
              }).catch(() => {});

              const userRef = doc(db, 'users', user.uid);
              updateDoc(userRef, {
                currentPlaybackState: {
                  videoId: payload.videoId,
                  status: 'PLAYING',
                  startTime: songStart,
                  endTime: songEnd,
                  startedAt: Date.now(),
                  title: payload.title || payload.item?.title || '',
                }
              }).catch(() => {});
            } catch (e) {}
          }
          break;
        case 'PLAY_EFFECT':
          if (payload?.path) {
            // Guardián contra solapamiento/eco: si el mismo efecto se solicitó hace menos de 450ms, descartar
            const now = Date.now();
            if (
              lastPlayedEffectRef.current.path === payload.path &&
              now - lastPlayedEffectRef.current.timestamp < 450
            ) {
              console.log('🔇 [Audio] Efecto duplicado suprimido (anti-eco):', payload.path);
              break;
            }
            lastPlayedEffectRef.current = { id: payload.id, path: payload.path, timestamp: now };

            try {
              if (window._activeRemoteHowl) {
                try {
                  window._activeRemoteHowl.stop();
                  window._activeRemoteHowl.unload();
                } catch (e) {}
                window._activeRemoteHowl = null;
              }

              // Persistir en Firestore para que todos los dispositivos /remote se sincronicen
              if (user?.uid) {
                updateDoc(doc(db, 'users', user.uid), {
                  activeEffect: {
                    id: payload.id,
                    path: payload.path,
                    duration: payload.duration || 3,
                    timestamp: now,
                  }
                }).catch(() => {});
              }

              // Notificar a los EffectButtons en la consola Desktop para que se iluminen
              window.dispatchEvent(new CustomEvent('garaoke_play_effect', {
                detail: { id: payload.id, path: payload.path, duration: payload.duration, _fromRemote: true }
              }));

              const sound = new Howl({
                src: [`/assets/${payload.path}`],
                html5: false,
                volume: typeof payload.volume === 'number' ? payload.volume : 1,
                autoplay: true,
                onend: () => {
                  window._activeRemoteHowl = null;
                  window.dispatchEvent(new CustomEvent('garaoke_stop_effect', {
                    detail: { id: payload.id, path: payload.path, _fromRemote: true }
                  }));
                  if (user?.uid) {
                    updateDoc(doc(db, 'users', user.uid), { activeEffect: null }).catch(() => {});
                  }
                },
                onloaderror: () => {
                  window._activeRemoteHowl = null;
                  window.dispatchEvent(new CustomEvent('garaoke_stop_effect', {
                    detail: { id: payload.id, path: payload.path, _fromRemote: true }
                  }));
                  if (user?.uid) {
                    updateDoc(doc(db, 'users', user.uid), { activeEffect: null }).catch(() => {});
                  }
                }
              });
              window._activeRemoteHowl = sound;
            } catch (err) {
              console.warn('Error reproduciendo efecto remoto:', err);
            }
          }
          break;
        case 'STOP_EFFECT':
          if (window._activeRemoteHowl) {
            try {
              window._activeRemoteHowl.stop();
              window._activeRemoteHowl.unload();
            } catch (e) {}
            window._activeRemoteHowl = null;
          }
          window.dispatchEvent(new CustomEvent('garaoke_stop_effect', {
            detail: { id: payload?.id, path: payload?.path, _fromRemote: true }
          }));
          if (user?.uid) {
            updateDoc(doc(db, 'users', user.uid), { activeEffect: null }).catch(() => {});
          }
          break;
        case 'CHANGE_INTRO':
          if (payload?.introId) {
            changeIntro(payload.introId);
          }
          break;
        case 'CHANGE_TRANSITION':
          if (payload?.transitionId) {
            changeTransition(payload.transitionId);
          }
          break;
        case 'TRIGGER_INTRO':
          openVideoPlayer(payload?.videoId || currentIntroVideo, false, false, 'intro');
          break;
        case 'TRIGGER_TRANSITION':
          openVideoPlayer(payload?.videoId || currentTransitionVideo, false, false, 'transition');
          break;
        case 'TOGGLE_OVERLAY_QR':
          showHideModalQR();
          break;
        case 'TOGGLE_OVERLAY_MESSAGE':
          showHideModalMessage(payload?.message);
          break;
        case 'TOGGLE_OVERLAY_VOTES':
          showHideModalVotesTable();
          break;
        case 'TRIGGER_MANUAL_VOTATION':
          triggerManualVotation();
          break;
        case 'PLAYLIST_UPDATED':
          if (user?.uid && typeof getUserSettings === 'function') {
            getUserSettings(user.uid);
          }
          break;
        case 'SELECT_PLAYLIST':
          if (payload?.playlistId) {
            setCurrentPlaylist(payload.playlistId);
          }
          break;
        case 'SET_EFFECTS_VOLUME':
          if (typeof payload?.volume === 'number') {
            try { localStorage.setItem('garaoke_effects_volume', String(payload.volume)); } catch (e) {}
            window.dispatchEvent(new CustomEvent('garaoke_set_effects_volume', {
              detail: { volume: payload.volume }
            }));
            if (user?.uid) {
              updateDoc(doc(db, 'users', user.uid), { effectsVolume: payload.volume }).catch(() => {});
            }
          }
          break;
        case 'SET_SCREEN_VOLUME':
          if (typeof payload?.volume === 'number') {
            handleUpdateScreenSettings({ volume: payload.volume }, true);
          }
          break;
        default:
          break;
      }
    };

    // 1. Suscripción nativa a Firestore (Soporte Cloud garantizado en Netlify)
    let unsubscribeFirestore = () => {};
    try {
      const userRef = doc(db, 'users', user.uid);
      unsubscribeFirestore = onSnapshot(userRef, (docSnap) => {
        if (!docSnap.exists()) return;
        const data = docSnap.data();

        // Sincronizar selección de intro y transición si cambió desde el control remoto
        if (data.currentIntroVideo) {
          setCurrentIntroVideo(prev => {
            if (prev !== data.currentIntroVideo) {
              try { localStorage.setItem('garaoke_current_intro', data.currentIntroVideo); } catch (e) {}
              notifyPlayer({ type: 'changeIntro', introId: data.currentIntroVideo });
              return data.currentIntroVideo;
            }
            return prev;
          });
        }
        if (data.currentTransitionVideo) {
          setCurrentTransitionVideo(prev => {
            if (prev !== data.currentTransitionVideo) {
              try { localStorage.setItem('garaoke_current_transition', data.currentTransitionVideo); } catch (e) {}
              notifyPlayer({ type: 'changeTransition', transitionId: data.currentTransitionVideo });
              return data.currentTransitionVideo;
            }
            return prev;
          });
        }

        // Sincronizar volumen de efectos de sonido si cambió desde el control remoto
        if (typeof data.effectsVolume === 'number') {
          try { localStorage.setItem('garaoke_effects_volume', String(data.effectsVolume)); } catch (e) {}
          window.dispatchEvent(new CustomEvent('garaoke_set_effects_volume', {
            detail: { volume: data.effectsVolume }
          }));
        }

        // Sincronizar estado de presencia del Control Remoto Móvil (/remote)
        if (data.remoteActive !== undefined) {
          remoteLastSeenRef.current = data.remoteLastSeen || 0;
          const isRecent = Boolean(data.remoteActive && (Date.now() - (data.remoteLastSeen || 0) < 35000));
          setIsRemoteActive(isRecent);
        } else {
          setIsRemoteActive(false);
        }

        // Si hay un comando remoto emitido desde el smartphone
        if (data.lastRemoteCommand?.commandId) {
          const { commandId, action, payload, timestamp } = data.lastRemoteCommand;
          const effectiveCmdId = commandId || payload?.commandId;
          if (shouldExecuteCommand(effectiveCmdId, timestamp)) {
            handleExecuteCommand(action, payload);
          }
        }
      }, (err) => {
        console.warn('Snapshot error on user remote commands:', err);
      });
    } catch (e) {
      console.warn('Firestore snapshot setup error:', e);
    }

    // 2. Suscripción opcional a backend Express SSE (para latencia ultra baja en red local o Render)
    let eventSource = null;
    if (isSyncBackendAvailable) {
      try {
        eventSource = new EventSource(`${apiBaseUrl}/sync/events/${user.uid}`);
        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            if (data.type === 'INITIAL_STATE' || data.type === 'STATE_CHANGE') {
              if (data.state?.videoId) {
                setVideoId(data.state.videoId);
              }
            }

            if (data.type === 'COMMAND_RECEIVED' && data.command) {
              const { action, payload, id, commandId } = data.command;
              const effectiveCmdId = commandId || id || payload?.commandId;
              if (shouldExecuteCommand(effectiveCmdId, data.command.timestamp || Date.now())) {
                handleExecuteCommand(action, payload);
              }
            }
          } catch (err) {}
        };

        eventSource.onerror = () => {
          // Si el backend no es SSE (por ejemplo en Netlify estático que responde HTML), cerrar para no ciclar errores
          if (eventSource) {
            try { eventSource.close(); } catch (e) {}
          }
        };
      } catch (e) {}
    }

    return () => {
      unsubscribeFirestore();
      if (eventSource) eventSource.close();
    };
  }, [user?.uid, location.pathname, openVideoPlayer, currentIntroVideo, currentTransitionVideo, showHideModalQR, showHideModalMessage, showHideModalVotesTable, triggerManualVotation, changePlaylistItem, setCurrentPlaylist, changeIntro, changeTransition, shouldExecuteCommand]);

  // ── Sincronizar efectos de sonido disparados o detenidos localmente en Desktop hacia Firestore ──
  useEffect(() => {
    if (!user?.uid) return;
    if (location.pathname.startsWith('/remote') || location.pathname.startsWith('/monitor')) {
      return;
    }

    const handleDesktopPlayEffect = (e) => {
      if (e.detail?._fromRemote) return;
      if (window._activeRemoteHowl) {
        try { window._activeRemoteHowl.stop(); } catch (err) {}
      }
      if (user?.uid) {
        updateDoc(doc(db, 'users', user.uid), {
          activeEffect: {
            id: e.detail?.id || null,
            path: e.detail?.path || null,
            duration: e.detail?.duration || 3,
            timestamp: Date.now(),
          }
        }).catch(() => {});
      }
    };

    const handleDesktopStopEffect = (e) => {
      if (e.detail?._fromRemote) return;
      if (window._activeRemoteHowl) {
        try { window._activeRemoteHowl.stop(); } catch (err) {}
      }
      if (user?.uid) {
        updateDoc(doc(db, 'users', user.uid), {
          activeEffect: null,
        }).catch(() => {});
      }
    };

    const handleDesktopEffectsVolume = (e) => {
      if (!e.detail?._local) return;
      if (user?.uid) {
        updateDoc(doc(db, 'users', user.uid), {
          effectsVolume: e.detail.volume,
        }).catch(() => {});
      }
    };

    window.addEventListener('garaoke_play_effect', handleDesktopPlayEffect);
    window.addEventListener('garaoke_stop_effect', handleDesktopStopEffect);
    window.addEventListener('garaoke_set_effects_volume', handleDesktopEffectsVolume);
    return () => {
      window.removeEventListener('garaoke_play_effect', handleDesktopPlayEffect);
      window.removeEventListener('garaoke_stop_effect', handleDesktopStopEffect);
      window.removeEventListener('garaoke_set_effects_volume', handleDesktopEffectsVolume);
    };
  }, [user?.uid, location.pathname]);

  const handleGoogleSignOut = useCallback(() => {

    signOut();
    fullReset();
  }, [signOut]);




  useEffect(() => {
    if (user && user.uid) {
      getUserSettings(user.uid);
    }
  }, [user, getUserSettings]);

  // Sincronizar la playlist activa y persistir en Firestore (currentPlaylist + playlist) para el Remote
  useEffect(() => {
    const activeMyPl = myPlaylists.find(p => p.id === currentPlaylist || p.name === currentPlaylist);
    let activeItems = [];
    if (activeMyPl) {
      activeItems = activeMyPl.items || [];
    } else {
      const userPreset = userPresetPlaylists.find(p => p.name === currentPlaylist);
      if (userPreset && userPreset.items) {
        activeItems = userPreset.items;
      } else {
        const originPreset = originPresetPlaylists.find(p => p.name === currentPlaylist);
        if (originPreset && originPreset.items) {
          activeItems = originPreset.items;
        }
      }
    }
    setPlaylist(activeItems);

    if (user?.uid && currentPlaylist) {
      try {
        const userRef = doc(db, 'users', user.uid);
        updateDoc(userRef, {
          currentPlaylist,
          playlist: activeItems,
        }).catch(() => {});
      } catch (e) {}
    }
  }, [currentPlaylist, myPlaylists, userPresetPlaylists, originPresetPlaylists, user?.uid]);

  /* eslint-enable */

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#000', color: '#fff' }}>
        <CircularProgress style={{ marginRight: 10 }} />
        Cargando...
      </Box>
    );
  }

  const pannelComponent = (
    <Pannel
      isRemoteActive={isRemoteActive}
      playlist={playlist}
      setPlaylist={handleSaveUserPlaylist}
      currentPlaylist={currentPlaylist}
      setCurrentPlaylist={setCurrentPlaylist}
      myPlaylists={myPlaylists}
      setMyPlaylists={setMyPlaylists}
      onCreatePlaylist={handleCreatePlaylist}
      onRenamePlaylist={handleRenamePlaylist}
      onDeletePlaylist={handleDeletePlaylist}
      onAddToPlaylist={handleAddToUserPlaylist}
      setKeySearch={setKeySearch}
      keySearch={keySearch}
      videosSearchResponse={videosSearchResponse}
      setVideosSearchResponse={setVideosSearchResponse}
      previewVideId={previewVideId}
      onIsKaraokeOnly={setIsKaraokeOnly}
      isKaraokeOnly={isKaraokeOnly}
      setPreviewVideoId={setPreviewVideoId}
      setCurrentIntroVideo={changeIntro}
      setCurrentTransitionVideo={changeTransition}
      currentIntroVideo={currentIntroVideo}
      currentTransitionVideo={currentTransitionVideo}
      user={user}
      userSettings={userSettings}
      openVideoPlayer={openVideoPlayer}
      showHideModalQR={showHideModalQR}
      showHideModalMessage={showHideModalMessage}
      showModalMessage={showModalMessage}
      showModalQR={showModalQR}
      showModalVotesTable={showModalVotesTable}
      videoPlayer={videoPlayer}
      setSnackMessage={setMessage}
      selectedApiKey={selectedApiKey}
      setSelectedApiKey={setSelectedApiKey}
      logger={logger}
      userPresetPlaylists={userPresetPlaylists}
      setUserPresetPlaylists={setUserPresetPlaylists}
      originPresetPlaylists={originPresetPlaylists}
      setOriginPresetPlaylists={setOriginPresetPlaylists}
      originPresetPlaylist={originPresetPlaylist}
      setOriginPresetPlaylist={setOriginPresetPlaylist}
      userPresetPlaylist={userPresetPlaylist}
      setUserPresetPlaylist={setUserPresetPlaylist}
      handleGoogleSignOut={handleGoogleSignOut}
      allowInteractivity={allowInteractivity}
      setCurrentPlaylistItem={changePlaylistItem}
      currentPlaylistItem={currentPlaylistItem}
      setVideoId={setVideoId}
      videoId={videoId}
      showHideModalVotesTable={showHideModalVotesTable}
      hasVotes={hasVotes}
      onResetVotes={handleResetVotes}
      auth={auth}
      saveUserSettings={saveUserSettings}
      setShowControlsPlayer={setShowControlsPlayer}
      showControlsPlayer={showControlsPlayer}
      setAllowInteractivity={setAllowInteractivity}
      screenSettings={screenSettings}
      onUpdateScreenSettings={handleUpdateScreenSettings}
      onResetScreenSettings={handleResetScreenSettings}
      updateUserField={updateUserField}
      isIntegratedScreen={isIntegratedScreen}
      onToggleIntegratedScreen={handleToggleIntegratedScreen}
      onTriggerManualVotation={triggerManualVotation}
      continuousPlay={continuousPlay}
      onToggleContinuousPlay={handleToggleContinuousPlay}
      crossfadeEnabled={crossfadeEnabled}
      onToggleCrossfade={handleToggleCrossfade}
      crossfadeDuration={crossfadeDuration}
      onUpdateCrossfadeDuration={handleUpdateCrossfadeDuration}
      onPlayNextSong={handlePlayNextSong}
    />
  );

  const renderConsoleOrMobileNotice = () => {
    if (isMobileScreen && !bypassMobileNotice) {
      return <MobileConsoleNotice onBypass={() => setBypassMobileNotice(true)} />;
    }
    return pannelComponent;
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <SnackMessage message={message} setMessage={setMessage} duration={message.duration} />
      <CssBaseline />
      <Routes>
        {/* Ruta raíz:
            1. Si no hay usuario -> Landing
            2. Si hay usuario y tiene plan activo:
               - Móvil -> Aviso de consola grande + enlace a Control Remoto
               - Desktop -> Consola / Panel
            3. Si hay usuario pero no tiene plan -> Pricing */}
        <Route
          path="/"
          element={
            !user ? (
              <Landing />
            ) : hasActivePlan() ? (
              renderConsoleOrMobileNotice()
            ) : (
              <Navigate to="/pricing" replace />
            )
          }
        />

        {/* Landing Page explícita */}
        <Route path="/landing" element={<Landing />} />

        {/* Registro explícito */}
        <Route path="/register" element={<Register />} />

        {/* Login directo */}
        <Route path="/login" element={<Login />} />

        {/* Pricing / Planes de suscripción */}
        <Route path="/pricing" element={<Pricing />} />

        {/* Soporte y Feedback (modelo Fotoevento) */}
        <Route path="/support" element={<Support />} />

        {/* Dashboard / Panel: protegido (en móvil muestra aviso o landing si deslogueado) */}
        <Route
          path="/dashboard"
          element={
            !user ? (
              <Landing />
            ) : hasActivePlan() ? (
              renderConsoleOrMobileNotice()
            ) : (
              <Navigate to="/pricing" replace />
            )
          }
        />

        {/* Configuración: redirige a dashboard abriendo el nuevo modal con fondo difuminado */}
        <Route
          path="/config"
          element={
            user ? (
              <Navigate to="/dashboard?config=true" replace />
            ) : (
              <Navigate to="/login?returnTo=/dashboard?config=true" replace />
            )
          }
        />

        {/* Player - ventana secundaria para TV / proyector */}
        <Route path="/player" element={<Player 
          userId={user?.uid}
          updateUserField={updateUserField} 
          currentPlaylistItem={currentPlaylistItem} 
          currentTransitionVideo={currentTransitionVideo}
          currentIntroVideo={currentIntroVideo}
          allowInteractivity={allowInteractivity}
          showControlsPlayer={showControlsPlayer}
          setVideoId={setVideoId}
          videoId={videoId}
          continuousPlay={continuousPlay}
          crossfadeEnabled={crossfadeEnabled}
          crossfadeDuration={crossfadeDuration}
          onPlayNextSong={handlePlayNextSong}
        />} 
        />

        {/* Monitor para teléfonos (invitados / QR) */}
        <Route path="/monitor" element={<Monitor user={user} updateUserField={updateUserField} />} />

        {/* Control Remoto Móvil (anfitrión / usuario registrado) */}
        <Route path="/remote" element={<RemoteControl />} />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />

      </Routes>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <Router>
        <ScrollToTop />
        <RouteTracker />
        <AuthProvider>
          <MainApp />
        </AuthProvider>
      </Router>
    </I18nProvider>
  );
}
